import { useI18n }                from '../../../../shared/i18n/useI18n.js'
import { useProductStrings }      from '../../../../shared/i18n/useProductStrings.js'

import { formatPriceLabelShort }  from '../../../../shared/utils/priceUtils.js'

import { areFilterConfigsEqual}   from '../../utils/filterUtils.js'
import { 
  formatMainBarLabel, 
  formatSingleVal, 
  getMultiSelectOptions,
  isSelfLabeled }                 from '../../utils/filterFormatters.js'
import { 
  FILTER_IDS, 
  BASE_MAIN_FILTER_IDS, 
  COMPONENT_TYPES }               from '../../constants/filterConstants.js'

import { FilterCard }     from './FilterCard.jsx'
import { Badge }          from '../../../../shared/ui/Badge/index.js'
import { Button }         from '../../../../shared/ui/Button/index.js'
import { Filter, Search } from '../../../../shared/ui/icons/index.js'
import { Input }          from '../../../../shared/ui/Input/index.js'
import { MultiSelect }    from '../../../../shared/ui/MultiSelect/index.js'
import { RangeSlider }    from '../../../../shared/ui/RangeSlider/index.js'
import { Tooltip }        from '../../../../shared/ui/Tooltip/Tooltip.jsx'

import styles from './CatalogMainBar.module.scss'

/**
 * Desktop / tablet main filter bar.
 *
 * Row 1: "Find unit" heading + up to 3 inline RangeSlider slots + CTA icon button with
 *        active-count Badge.
 * Promoted grid: non-slider main-bar filters rendered as FilterCards in a CSS grid
 *
 * Intentionally does NOT consume CatalogContext — all data is passed as props by the parent
 *
 * @param {object}   props
 * @param {number}   props.activeFiltersCount
 * @param {object}   props.catalogFilters
 * @param {object}   [props.defaultFiltersConfig={}]  - CMS default filter state (overlay applied)
 * @param {object}   props.filtersConfig             - current active filter values
 * @param {object}   props.fullFiltersRange           - full data range per filter id
 * @param {boolean}  props.isExpanded                - drives CTA aria-expanded state
 * @param {(id: string, value: *) => void} props.onFilterChange
 * @param {() => void} props.onToggleExpand
 */
export function CatalogMainBar({
  activeFiltersCount,
  catalogFilters,
  defaultFiltersConfig = {},
  filtersConfig,
  fullFiltersRange,
  isExpanded,
  onFilterChange,
  onToggleExpand,
}) {
  // ── i18n ──────────────────────────────────────────────────────────────────
  const { t } = useI18n()
  const getProductsCategoryString = useProductStrings();

  const { mainFilters, promotedFilters, additionalFilters} = catalogFilters

  return (
    <div>
      {/* ── Row 1: title + sliders + CTA ── */}
      <div className={styles.bar}>
        <p className={styles.title}><span className={styles.titleText}>{getProductsCategoryString('find', 'default')}</span></p>

        <div className={styles.sliders}>
          {mainFilters.map(entry => {
            if (!entry) return null
            const { id, labelKey } = entry
            const range = fullFiltersRange?.[id]
            const value = filtersConfig?.[id]
            const name = t(labelKey)

            // price === null means no valid price data at all → show contact-only text
            const isPriceNull = id === FILTER_IDS.PRICE_RANGE && range === null
            // flat range (min === max) → show single value, slider disabled
            const isFlat      = Array.isArray(range) && range[0] === range[1]
            const isDisabled  = isPriceNull || isFlat

            const defaultVal = defaultFiltersConfig?.[id]
            // active = user moved slider away from the CMS default
            const isActive = !isDisabled
              && Array.isArray(value) && Array.isArray(defaultVal)
              && (value[0] !== defaultVal[0] || value[1] !== defaultVal[1])

            return (
              <div key={id} data-filterid={id} className={styles.sliderSlot}>
                    <div className={styles.sliderLabelRow}>
                      <span className={`${styles.sliderName}${isActive ? ` ${styles.sliderLabelActive}` : ''}`}>{name}</span>

                      {isPriceNull ? (
                        <span className={styles.sliderDisabledText}>{t('common.ask_for_price')}</span>
                      ) : isFlat ? (
                        <span className={styles.sliderValues}>{formatSingleVal(id, range?.[0])}</span>
                      ) : (
                        <span className={`${styles.sliderValues}${isActive ? ` ${styles.sliderValuesActive}` : ''}`}>{formatMainBarLabel(id, value)}</span>
                      )}
                    </div>

                    {!isPriceNull && (
                      <RangeSlider
                        defaultValue={range ?? [0, 1]}
                        value={value ?? range ?? [0, 1]}
                        onChange={val => onFilterChange(id, val)}
                        disabled={isDisabled}
                        isActive={isActive}
                        step={entry?.step}
                        formatValue={
                          id === FILTER_IDS.PRICE_RANGE ? formatPriceLabelShort :
                          id === FILTER_IDS.AREA  ? v => `${v} m²` :
                          undefined
                        }
                      />
                    )}
              </div>
            )
          })}
        </div>

        <Tooltip 
          title={isExpanded ? t('catalog.hide_extra_filters') : t('catalog.show_extra_filters')}
          placement='bottom'
        >
          <div className={styles.ctaWrap}>
            <Button
              cta
              type="button"
              kind="icon"
              size="large"
              onClick={onToggleExpand}
              aria-expanded={isExpanded}
              aria-controls="catalog-additional-panel"
              aria-label={t('catalog.toggle_extra_filters')}
            >
              <Filter />
            </Button>
            <Badge count={activeFiltersCount} className={styles.ctaBadge} />
          </div>
        </Tooltip>
      </div>

      {/* ── Promoted grid: non-slider main bar filter cards ── */}
      {promotedFilters.length > 0 && (
        <div className={styles.promotedGrid}>
          {promotedFilters.map(entry => {
            if (!entry) return null
            const { id, labelKey } = entry
            const value  = filtersConfig?.[id]
            const range  = fullFiltersRange?.[id]
            const label  = t(labelKey)

            // Active = value differs from CMS default
            const isActive = !areFilterConfigsEqual(value, defaultFiltersConfig?.[id])

            return (
              <FilterCard key={id} label={label} isActive={isActive} hideLabel={isSelfLabeled(entry)}>
                {entry.component === COMPONENT_TYPES.TEXT_SEARCH && (
                  <Input
                    value={value ?? ''}
                    onChange={v => onFilterChange(id, v)}
                    placeholder={entry.placeholderKey ? t(entry.placeholderKey) : t('catalog.search_placeholder')}
                    isActive={isActive}
                    suffix={<Search />}
                    trimOnBlur
                  />
                )}
                {entry.component === COMPONENT_TYPES.MULTI_SELECT && (
                  <MultiSelect
                    value={Array.isArray(value) ? value.map(String) : []}
                    onChange={v => onFilterChange(id, v)}
                    options={getMultiSelectOptions(entry, fullFiltersRange)}
                    variant="summary"
                    label={entry.labelKey ? t(entry.labelKey) : undefined}
                    isActive={isActive}
                    placeholder={entry.placeholderKey ? t(entry.placeholderKey) : undefined}
                  />
                )}
              </FilterCard>
            )
          })}
        </div>
      )}
    </div>
  )
}
